import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserPlus,
  Trash2,
  Mail,
  Shield,
  ShieldCheck,
  User,
  Loader2,
  X,
  Eye,
  EyeOff,
  KeyRound,
} from "lucide-react";
import { auth, formatApiError } from "../../lib/api";

const ROLE_META = {
  super_admin: { label: "Super admin", color: "text-brand-orange", bg: "bg-brand-orange/15", border: "border-brand-orange/30", icon: ShieldCheck },
  admin: { label: "Admin", color: "text-blue-300", bg: "bg-blue-500/15", border: "border-blue-500/30", icon: Shield },
  assistant: { label: "Asistente", color: "text-emerald-300", bg: "bg-emerald-500/15", border: "border-emerald-500/30", icon: User },
};

export default function UsersPanel({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setError("");
    try {
      const list = await auth.listUsers();
      setUsers(list || []);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (user) => {
    if (!window.confirm(`¿Eliminar a "${user.name}" (${user.email})?`)) return;
    setDeletingId(user.id);
    try {
      await auth.deleteUser(user.id);
      setUsers((arr) => arr.filter((u) => u.id !== user.id));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-white tracking-tight">
            Usuarios
          </h2>
          <p className="mt-1 text-sm text-white/55">
            Gestiona quién puede acceder al panel de leads.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="users-create-btn"
          className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-full px-5 py-2.5 text-sm font-medium transition-all shadow-[0_0_18px_rgba(249,115,22,0.35)] hover:shadow-[0_0_28px_rgba(249,115,22,0.55)]"
        >
          <UserPlus size={15} />
          <span className="hidden sm:inline">Nuevo usuario</span>
          <span className="sm:hidden">Nuevo</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="rounded-2xl bg-surface/60 border border-white/[0.07] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-white/40 text-sm">Cargando...</div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-white/45 text-sm">No hay usuarios.</div>
        ) : (
          <ul className="divide-y divide-white/5" data-testid="users-list">
            {users.map((u) => {
              const meta = ROLE_META[u.role] || ROLE_META.assistant;
              const Icon = meta.icon;
              const isSelf = u.id === currentUser.id;
              const created = u.created_at ? new Date(u.created_at) : null;
              return (
                <li
                  key={u.id}
                  className="px-5 md:px-6 py-4 flex items-center gap-4 hover:bg-white/[0.02] transition-colors"
                  data-testid={`user-row-${u.email}`}
                >
                  <div className={`w-10 h-10 rounded-full ${meta.bg} border ${meta.border} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={16} className={meta.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-white">{u.name}</span>
                      {isSelf && (
                        <span className="text-[10px] uppercase tracking-wider text-white/40 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">
                          tú
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full ${meta.bg} ${meta.color} border ${meta.border}`}>
                        {meta.label}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-white/50 flex items-center gap-1.5">
                      <Mail size={11} />
                      {u.email}
                      {created && (
                        <span className="text-white/30 ml-2">· creado {created.toLocaleDateString()}</span>
                      )}
                    </p>
                  </div>
                  {!isSelf && (
                    <button
                      onClick={() => handleDelete(u)}
                      disabled={deletingId === u.id}
                      data-testid={`user-delete-${u.email}`}
                      className="w-9 h-9 rounded-full hover:bg-red-500/15 hover:text-red-400 text-white/50 flex items-center justify-center transition-colors disabled:opacity-50"
                      title="Eliminar usuario"
                    >
                      {deletingId === u.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <AnimatePresence>
        {showCreate && (
          <CreateUserModal
            onClose={() => setShowCreate(false)}
            onCreated={(newUser) => {
              setUsers((arr) => [...arr, newUser]);
              setShowCreate(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("assistant");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (name.trim().length < 1) return setError("Falta el nombre.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError("Email inválido.");
    if (password.length < 6) return setError("La contraseña debe tener mínimo 6 caracteres.");
    setSubmitting(true);
    try {
      const u = await auth.createUser({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        role,
      });
      onCreated(u);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="create-user-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.97 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-md bg-[#0E1015] border border-white/10 rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-5 border-b border-white/5 flex items-start justify-between">
          <div>
            <span className="text-[11px] uppercase tracking-wider text-brand-orange/90 font-medium">
              Nuevo usuario
            </span>
            <h3 className="mt-1 text-xl font-semibold text-white">Agregar asistente o admin</h3>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1" data-testid="create-user-close" aria-label="cerrar">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} noValidate className="p-6 space-y-4" data-testid="create-user-form">
          <ModalField
            id="user-name"
            label="Nombre"
            icon={User}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej. María Asistente"
            autoComplete="name"
            autoFocus
          />
          <ModalField
            id="user-email"
            label="Email"
            icon={Mail}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="maria@vexlyapp.com"
            autoComplete="off"
          />
          <div>
            <label htmlFor="user-password" className="block text-xs font-medium text-white/55 mb-1.5 uppercase tracking-wider">
              Contraseña
            </label>
            <div className="relative">
              <KeyRound size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
              <input
                id="user-password"
                data-testid="create-user-password"
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                autoComplete="new-password"
                className="w-full pl-10 pr-10 py-3 rounded-xl bg-white/[0.04] border border-white/10 focus:border-brand-orange/60 text-white text-[15px] placeholder:text-white/30 outline-none transition-colors"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                tabIndex={-1}
              >
                {show ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-white/55 mb-2 uppercase tracking-wider">Rol</label>
            <div className="grid grid-cols-3 gap-2" data-testid="create-user-role-select">
              {[
                { value: "assistant", label: "Asistente", help: "Solo ver leads" },
                { value: "admin", label: "Admin", help: "Ver leads" },
                { value: "super_admin", label: "Super", help: "+ Gestiona usuarios" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRole(opt.value)}
                  data-testid={`create-user-role-${opt.value}`}
                  className={`text-left px-3 py-2.5 rounded-xl border transition-colors ${
                    role === opt.value
                      ? "bg-brand-orange/15 border-brand-orange/40 text-white"
                      : "bg-white/[0.03] border-white/10 text-white/70 hover:text-white hover:bg-white/[0.06]"
                  }`}
                >
                  <p className="text-sm font-medium">{opt.label}</p>
                  <p className="text-[10px] text-white/45 mt-0.5">{opt.help}</p>
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-red-400" data-testid="create-user-error">{error}</p>}

          <div className="flex gap-2.5 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-5 py-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="create-user-submit"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-full px-5 py-3 text-sm font-medium transition-colors disabled:opacity-70"
            >
              {submitting ? <><Loader2 size={15} className="animate-spin" />Creando...</> : <>Crear usuario</>}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function ModalField({ id, label, icon: Icon, ...rest }) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-white/55 mb-1.5 uppercase tracking-wider">
        {label}
      </label>
      <div className="relative">
        <Icon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
        <input
          id={id}
          data-testid={id}
          {...rest}
          className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 focus:border-brand-orange/60 text-white text-[15px] placeholder:text-white/30 outline-none transition-colors"
        />
      </div>
    </div>
  );
}
